import React, { FC } from "react";
import { CreateChallengeFormTypes } from "@/features/challenge/utils/CreateChallengeTypes";
import DateRangePicker from "@/components/DateRangePicker/DateRangePicker";
import FormErrorMessage from "@/components/FormErrorMessage/FormErrorMessage";
import { ImageUpload } from "@/components/ImageUpload/ImageUpload";
import {
  Flex,
  TextField,
  Text,
  TextArea,
  IconButton,
  Box,
  AspectRatio,
} from "@radix-ui/themes";
import { Cross1Icon, ImageIcon } from "@radix-ui/react-icons";

export const CreateChallengeStep2: FC<{
  values: CreateChallengeFormTypes;
  errors: any;
  setFieldValue: (targetName: string, value: any) => Promise<void>;
  isEdit: boolean;
}> = ({ values, setFieldValue, errors, isEdit }): JSX.Element => {
  const getChallengeImg = () => {
    if (values.logoUrl && values.logoUrl.name) {
      return URL.createObjectURL(values.logoUrl);
    }
    if (values.logoUrl && String(values.logoUrl)) {
      return values.logoUrl;
    }
  };

  const challengeImage =
    values.logoUrl && values.logoUrl.name
      ? URL.createObjectURL(values.logoUrl)
      : values.logoUrl;

  return (
    <>
      <Flex direction="column" gap="3">
        {errors && errors["name"] && (
          <FormErrorMessage
            text={errors["name"] as string}
            isVisible={errors["name"]}
          />
        )}

        {errors && errors["goal"] && (
          <FormErrorMessage
            text={errors["goal"] as string}
            isVisible={errors["goal"]}
          />
        )}

        {errors && errors["description"] && (
          <FormErrorMessage
            text={errors["description"] as string}
            isVisible={errors["description"]}
          />
        )}

        {errors && errors["logoUrl"] && (
          <FormErrorMessage
            text={errors["logoUrl"] as string}
            isVisible={errors["logoUrl"]}
          />
        )}
      </Flex>

      <Flex direction="column" gap="3">
        {/*  Duration */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="datepicker" size="2">
            Duration{" "}
            {isEdit && (
              <Text size="1" color="gray">
                ( cannot be edited )
              </Text>
            )}
          </Text>
          <DateRangePicker
            errors={errors}
            name="duration"
            value={values.duration}
            setFieldValue={setFieldValue}
            isDisabled={isEdit}
          />
        </Flex>

        {/*  Name input */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="challenge-name" size="2">
            Challenge name
          </Text>
          <TextField.Root
            id="challenge-name"
            placeholder="Enter a name for the challenge"
            variant="soft"
            color="gray"
            value={values.name}
            onChange={(e) => setFieldValue("name", e.target.value)}
          />
        </Flex>

        {/*  Goal input */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="challenge-goal" size="2">
            Objective
          </Text>
          <TextField.Root
            id="challenge-goal"
            placeholder="Goal"
            variant="soft"
            color="gray"
            value={values.goal}
            onChange={(e) => setFieldValue("goal", e.target.value)}
          />
        </Flex>

        {/* Description */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="challenge-desc" size="2">
            Description
          </Text>
          <TextArea
            color="gray"
            spellCheck={false}
            id="challenge-desc"
            variant="soft"
            rows={5}
            placeholder="Enter a description"
            value={values.description}
            onChange={(e) => setFieldValue("description", e.target.value)}
          />
        </Flex>

        {/* Image */}
        <Flex direction="column" gap="1">
          <Text as="label" size="2">
            Image
          </Text>
          <Flex gap="2">
            {/* How uploaded images look like */}
            {values.logoUrl && (
              <Box width="120px" height="120px" position="relative">
                <AspectRatio ratio={1 / 1}>
                  <img
                    src={
                      typeof challengeImage === "string" ? challengeImage : ""
                    }
                    alt={values.name || "Image"}
                    width="200"
                    height="200"
                    style={{
                      borderRadius: "var(--radius-2)",
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </AspectRatio>
                {/* Remove image button */}
                <Box position="absolute" inset="0.25rem 0.25rem auto auto">
                  <IconButton
                    highContrast
                    color="red"
                    variant="solid"
                    size="2"
                    onClick={() => {
                      setFieldValue("logoUrl", undefined);
                    }}
                  >
                    <Cross1Icon />
                  </IconButton>
                </Box>
              </Box>
            )}
            {!values.logoUrl && (
              <ImageUpload
                acceptType="image/*"
                setIsLoading={() => {}}
                setFieldValue={setFieldValue}
                setType={() => {}}
                value={values.logoUrl}
                name="logoUrl"
              >
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    border: "1px solid var(--gray-5)",
                    borderRadius: "var(--radius-2)",
                    width: "120px",
                    height: "120px",
                  }}
                >
                  <Box>
                    <IconButton
                      highContrast
                      color="gray"
                      variant="soft"
                      size="2"
                      onClick={() => {
                        // Trigger the file input click event
                        (
                          document.querySelector(
                            'input[type="file"]'
                          ) as HTMLInputElement
                        ).click();
                      }}
                    >
                      <ImageIcon />
                    </IconButton>
                  </Box>
                </Flex>
              </ImageUpload>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
